import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Checkbox } from '../../../../../components/ui/checkbox';
import {
  MANUAL_PASS_BASE_NAMES,
  type ManualPassBaseName,
} from './manualStatusConfigurationPasses';

export interface AddManualJobNumberPayload {
  jobNumber: string;
  selectedPasses: ManualPassBaseName[];
}

export type ManualJobPassDialogMode = 'add' | 'edit';

interface AddManualJobNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: ManualJobPassDialogMode;
  initialJobNumber?: string;
  initialSelectedPasses?: ManualPassBaseName[];
  onSave: (payload: AddManualJobNumberPayload) => void;
}

const PASS_CHECKBOX_COLUMNS: ManualPassBaseName[][] = [
  ['Root', 'Fill', 'Strip'],
  ['Hot', 'Cap', 'Weave'],
];

const createEmptyPassSelection = (): Record<ManualPassBaseName, boolean> =>
  MANUAL_PASS_BASE_NAMES.reduce(
    (state, name) => {
      state[name] = false;
      return state;
    },
    {} as Record<ManualPassBaseName, boolean>
  );

export const AddManualJobNumberDialog: React.FC<AddManualJobNumberDialogProps> = ({
  open,
  onOpenChange,
  mode = 'add',
  initialJobNumber = '',
  initialSelectedPasses = [],
  onSave,
}) => {
  const isEditMode = mode === 'edit';
  const [jobNumber, setJobNumber] = useState('');
  const [selectedPasses, setSelectedPasses] = useState<Record<ManualPassBaseName, boolean>>(
    createEmptyPassSelection
  );

  useEffect(() => {
    if (!open) return;

    setJobNumber(isEditMode ? initialJobNumber : '');
    setSelectedPasses(
      MANUAL_PASS_BASE_NAMES.reduce(
        (state, name) => {
          state[name] = isEditMode ? initialSelectedPasses.includes(name) : false;
          return state;
        },
        {} as Record<ManualPassBaseName, boolean>
      )
    );
  }, [open, isEditMode, initialJobNumber, initialSelectedPasses]);

  const handleCancel = () => {
    onOpenChange(false);
  };

  const togglePass = (baseName: ManualPassBaseName, checked: boolean) => {
    setSelectedPasses((current) => ({ ...current, [baseName]: checked }));
  };

  const handleSave = () => {
    const trimmedJob = (isEditMode ? initialJobNumber : jobNumber).trim();
    const passes = MANUAL_PASS_BASE_NAMES.filter((name) => selectedPasses[name]);
    if (!trimmedJob || passes.length === 0) return;

    onSave({
      jobNumber: trimmedJob,
      selectedPasses: passes,
    });
    onOpenChange(false);
  };

  const canSave =
    Boolean((isEditMode ? initialJobNumber : jobNumber).trim()) &&
    MANUAL_PASS_BASE_NAMES.some((name) => selectedPasses[name]);

  const dialogCopy = useMemo(
    () =>
      isEditMode
        ? {
            title: 'Edit Passes',
            description: 'Check or uncheck pass groups for this job number.',
            jobLabel: 'Job Number',
            bullets: [
              'Unchecking a pass removes only that pass_group_name (e.g. Fill rows only).',
              'Checked passes are kept/updated; duplicate rows for the same group stay one column.',
            ],
          }
        : {
            title: 'Add New Job Number',
            description: 'Enter a job number and select the passes to include.',
            jobLabel: 'Enter Job Number',
            bullets: [
              'Select one or more passes from the available list.',
              'Only checked passes will be saved for this job number.',
            ],
          },
    [isEditMode]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleCancel();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden bg-white">
        <DialogHeader className="px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue">
            {dialogCopy.title}
          </DialogTitle>
          <p className="text-[12px] text-heritage-grey mt-1">{dialogCopy.description}</p>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5 bg-page-bg">
          <div className="space-y-2">
            <Label className="text-[12px] font-medium text-text-grey">{dialogCopy.jobLabel}</Label>
            <Input
              value={isEditMode ? initialJobNumber : jobNumber}
              onChange={(event) => {
                if (isEditMode) return;
                const next = event.target.value;
                setJobNumber(next.startsWith(' ') ? next.trimStart() : next);
              }}
              placeholder="e.g. Job_num3"
              disabled={isEditMode}
              className="h-9 bg-white border-[#E2E5EA] text-[13px] text-text-grey placeholder:text-heritage-grey focus-visible:ring-hero-red/30 disabled:cursor-not-allowed disabled:opacity-70"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canSave) {
                  event.preventDefault();
                  handleSave();
                }
              }}
            />
          </div>

          <div className="overflow-hidden rounded-md border border-[#E2E5EA] bg-white">
            <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA]">
              <p className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                Select Passes
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 p-4">
              {PASS_CHECKBOX_COLUMNS.map((column, columnIndex) => (
                <div key={columnIndex} className="space-y-3">
                  {column.map((baseName) => (
                    <label
                      key={baseName}
                      htmlFor={`add-job-pass-${baseName}`}
                      className="flex items-center gap-3 rounded-md border border-[#E2E5EA] bg-white px-3 py-2.5 cursor-pointer hover:bg-page-bg transition-colors"
                    >
                      <Checkbox
                        id={`add-job-pass-${baseName}`}
                        checked={selectedPasses[baseName]}
                        onCheckedChange={(checked) => togglePass(baseName, checked === true)}
                        className="h-4 w-4 data-[state=checked]:bg-hero-red data-[state=checked]:border-hero-red data-[state=checked]:text-white"
                      />
                      <span className="text-[13px] font-medium text-text-grey">{baseName}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <ul className="list-disc space-y-1 pl-5 text-[11px] text-heritage-grey">
            {dialogCopy.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#E2E5EA] bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="h-9 px-4 text-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-xs disabled:opacity-50"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
